/* eslint-disable react-hooks/exhaustive-deps */
import { useBookingForm } from "../../../hooks/useBookingForm"

import { FormikProvider } from 'formik'
import { Check, ChevronLeft, ChevronRight, Loader2, QrCode, X } from 'lucide-react'
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Service } from "../../../types/service.type"
import { CustomForm } from "../../../components/custom-form"
import { DataContext, ProjectContext } from "../../../App"
import moment from "moment"
import { ConfirmationModal } from "../../../components/confirmation-modal"
import { ServicePickerModal } from "../../../components/service-picker-modal"
import { DatePickerField } from "../../../components/booking/DatePickerField"
import {
  getReadablePrimaryButtonStyle,
  resolveThemeColor,
} from "../../../utils/buttonColor"
import { firstAvailableDate, isDateAvailable } from "../../../utils/bookingDate"
import { decodeQrFromImageFile } from "../../../utils/customerQr"

const STEP_DETAILS = 0
const STEP_SERVICES = 1
const STEP_SCHEDULE = 2
const STEP_EXTRAS = 3
const STEP_REVIEW = 4

const STEP_IDS = ['details', 'services', 'schedule', 'extras', 'review'] as const

// Fields validated when leaving each step (the review step re-checks everything).
const STEP_FIELD_KEYS: string[][] = [
  ['customerName', 'customerPhoneNo', 'customerPhonePrefix', 'customerEmail'],
  ['serviceIds', 'isExternal', 'attendedAddress'],
  ['reservationDate', 'reservationTime'],
  [],
]

function RequiredMark({ title }: { title: string }) {
  return (
    <span className="ml-0.5 text-red-500" aria-hidden="true" title={title}>
      *
    </span>
  )
}

function BookingForm(
  { selectedServices, setSelectedServices }:
    { selectedServices: Service[], setSelectedServices: (services: Service[]) => void }
) {
  const {
    availableTimes,
    formik,
    customFormFields,
    getCustomFormErrors,
    operationHours,
    allowExternalBooking,
    bookService,
    isConfirmationModalOpen,
    setIsConfirmationModalOpen,
    closeConfirmationModal,
    isSubmitting,
    recentServices,
    lookupRecentBookings,
    applyCustomerQr,
    qrStatus,
    setQrStatus,
  } = useBookingForm(selectedServices, setSelectedServices)
  const qrInputRef = useRef<HTMLInputElement>(null)
  const { services, serviceTypes } = useContext(DataContext)
  const { project } = useContext(ProjectContext)
  const { t, i18n } = useTranslation('booking')

  const STEPS = useMemo(
    () =>
      STEP_IDS.map((id) => ({
        id,
        label: t(`steps.${id}`),
      })),
    [t, i18n.language]
  )

  const FIELD_LABELS = useMemo(
    (): Record<string, string> => ({
      serviceIds: t('fields.services'),
      reservationDate: t('fields.serviceDate'),
      reservationTime: t('fields.serviceTime'),
      customerName: t('fields.fullName'),
      customerPhoneNo: t('fields.phoneNumber'),
      customerEmail: t('fields.email'),
      isExternal: t('fields.location'),
      attendedAddress: t('fields.serviceAddress'),
    }),
    [t, i18n.language]
  )

  const [step, setStep] = useState(STEP_DETAILS)
  const stepContentRef = useRef<HTMLDivElement>(null)
  const [keyboardInset, setKeyboardInset] = useState(0)
  const [stepSummary, setStepSummary] = useState<{ name: string; label: string }[]>([])
  const [maxValidFrom, setMaxValidFrom] = useState<Date>(moment().toDate())
  const [minValidTo, setMinValidTo] = useState<Date>(moment().add(3, 'months').toDate())
  const [isServicePickerOpen, setIsServicePickerOpen] = useState(false)
  const [serviceSearchQuery, setServiceSearchQuery] = useState('')
  const [serviceCategoryTab, setServiceCategoryTab] = useState<'all' | string>('all')

  const serviceCategoryTabs = useMemo(() => {
    const tabs: { id: string; label: string }[] = []
    if (recentServices.length > 0) {
      tabs.push({ id: 'recent', label: t('recent') })
    }
    tabs.push({ id: 'all', label: t('all') })
    const knownIds = new Set(serviceTypes.map((st) => st.serviceTypeId))
    const sortedTypes = [...serviceTypes].sort((a, b) => a.type.localeCompare(b.type))
    sortedTypes.forEach((st) => {
      tabs.push({ id: st.serviceTypeId, label: st.type })
    })
    const orphanIds = Array.from(new Set(services.map((s) => s.serviceTypeId))).filter((id) => !knownIds.has(id))
    orphanIds.sort()
    orphanIds.forEach((id) => {
      const label = services.find((s) => s.serviceTypeId === id)?.serviceType?.trim() || t('other')
      tabs.push({ id, label })
    })
    return tabs
  }, [serviceTypes, services, recentServices, t, i18n.language])

  const filteredServices = useMemo(() => {
    const source =
      serviceCategoryTab === 'recent'
        ? recentServices
        : serviceCategoryTab === 'all'
          ? services
          : services.filter((s) => s.serviceTypeId === serviceCategoryTab)
    const q = serviceSearchQuery.trim().toLowerCase()
    if (!q) return source
    return source.filter((s) => s.title.toLowerCase().includes(q))
  }, [services, serviceSearchQuery, serviceCategoryTab, recentServices])

  const toggleServiceInPicker = (service: Service) => {
    if (selectedServices.some((s) => s.serviceId === service.serviceId)) {
      setSelectedServices(selectedServices.filter((s) => s.serviceId !== service.serviceId))
    } else {
      setSelectedServices([...selectedServices, service])
    }
  }

  const closeServicePicker = () => {
    setIsServicePickerOpen(false)
    setServiceSearchQuery('')
    setServiceCategoryTab(recentServices.length > 0 ? 'recent' : 'all')
  }

  const onHandleChange = (e: any) => {
    formik.handleChange(e)
  }

  const availabilityOptions = useMemo(
    () => ({ maxValidFrom, minValidTo, operationHours }),
    [maxValidFrom, minValidTo, operationHours]
  )

  const summaryForStep = (
    stepIndex: number,
    errors: Record<string, unknown>,
    customErrors: Record<string, string>
  ) => {
    const keys = STEP_FIELD_KEYS[stepIndex] || []
    const summary: { name: string; label: string }[] = []
    keys.forEach((key) => {
      if (errors[key]) summary.push({ name: key, label: FIELD_LABELS[key] || key })
    })
    if (stepIndex === STEP_EXTRAS) {
      Object.keys(customErrors).forEach((key) => {
        const label = customFormFields.find((f) => f.key === key)?.label || key
        summary.push({ name: `customForm.${key}`, label })
      })
    }
    return summary
  }

  const markStepTouched = (stepIndex: number, customErrors: Record<string, string>) => {
    const keys = STEP_FIELD_KEYS[stepIndex] || []
    const touched = keys.reduce((acc, key) => ({ ...acc, [key]: true }), {} as Record<string, boolean>)
    formik.setTouched({ ...formik.touched, ...touched }, false)
    if (stepIndex === STEP_EXTRAS) {
      Object.entries(customErrors).forEach(([key, message]) => {
        formik.setFieldError(`customForm.${key}`, message)
        formik.setFieldTouched(`customForm.${key}`, true, false)
      })
    }
  }

  const goToStep = (target: number) => {
    setStepSummary([])
    setStep(target)
  }

  const handleQrFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Reset immediately so re-picking the same file still fires a change event.
    event.target.value = ''
    if (!file) return

    setQrStatus({ state: 'loading', message: '' })
    try {
      const code = await decodeQrFromImageFile(file)
      if (!code) {
        setQrStatus({ state: 'error', message: t('qr.unreadable') })
        return
      }
      await applyCustomerQr(code)
    } catch (error) {
      console.error(error)
      setQrStatus({ state: 'error', message: t('qr.unreadable') })
    }
  }

  const goNext = async () => {
    const errors = (await formik.validateForm()) as Record<string, unknown>
    const customErrors = step === STEP_EXTRAS ? getCustomFormErrors() : {}
    markStepTouched(step, customErrors)

    const summary = summaryForStep(step, errors, customErrors)
    if (summary.length > 0) {
      setStepSummary(summary)
      return
    }

    // After contact details, look up recent bookings for returning customers.
    if (step === STEP_DETAILS) {
      lookupRecentBookings(formik.values.customerPhonePrefix + formik.values.customerPhoneNo)
    }

    setStepSummary([])
    setStep((prev) => Math.min(prev + 1, STEP_REVIEW))
  }

  const handleFinalSubmit = async () => {
    const errors = (await formik.validateForm()) as Record<string, unknown>
    const customErrors = getCustomFormErrors()

    formik.setTouched(
      Object.keys(formik.values).reduce((acc, key) => ({ ...acc, [key]: true }), {} as Record<string, boolean>),
      false
    )
    Object.entries(customErrors).forEach(([key, message]) => {
      formik.setFieldError(`customForm.${key}`, message)
      formik.setFieldTouched(`customForm.${key}`, true, false)
    })

    for (let i = 0; i < STEP_FIELD_KEYS.length; i++) {
      const summary = summaryForStep(i, errors, customErrors)
      if (summary.length > 0) {
        setStep(i)
        setStepSummary(summary)
        return
      }
    }

    setStepSummary([])
    formik.handleSubmit()
  }

  const handlePrimary = () => {
    if (step < STEP_REVIEW) {
      goNext()
    } else {
      handleFinalSubmit()
    }
  }

  // Keep the fixed action bar glued to the bottom of the visual viewport (top of the
  // keyboard) on mobile. offsetTop is included and we listen to scroll so the bar
  // follows the keyboard as the page scrolls.
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const update = () => {
      setKeyboardInset(Math.round(Math.max(0, window.innerHeight - vv.height - vv.offsetTop)))
    }
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    update()
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [])

  // Autofocus the first field whenever the active step changes, but only if it
  // lives in the step's first section (so lower fields like the address aren't grabbed).
  useEffect(() => {
    const container = stepContentRef.current
    if (!container) return
    const firstSection = container.querySelector<HTMLElement>('section')
    const focusable = firstSection?.querySelector<HTMLElement>(
      'input:not([disabled]), textarea:not([disabled]), select:not([disabled])'
    )
    focusable?.focus()
  }, [step])

  useEffect(() => {
    formik.setFieldValue('serviceIds', selectedServices.map((service) => service.serviceId))

    // Reset to default bounds, then narrow to the tightest window across services.
    setMaxValidFrom(moment().toDate())
    setMinValidTo(moment().add(3, 'months').toDate())

    selectedServices.forEach((service) => {
      if (service.validFrom) {
        setMaxValidFrom((prev) => {
          const serviceValidFrom = moment(service.validFrom).toDate()
          return serviceValidFrom > prev ? serviceValidFrom : prev
        })
      }
      if (service.validTo) {
        setMinValidTo((prev) => {
          const serviceValidTo = moment(service.validTo).toDate()
          return serviceValidTo < prev ? serviceValidTo : prev
        })
      }
    })
  }, [selectedServices])

  useEffect(() => {
    if (formik.values.reservationTime) return
    formik.setFieldValue('reservationTime', availableTimes?.[0]?.value)
  }, [availableTimes])

  // Keep the selected date valid: when the validity window or operation hours
  // change (e.g. switching location), snap to the first available date.
  useEffect(() => {
    const current = formik.values.reservationDate
    const currentIsValid =
      !!current && isDateAvailable(moment(current, 'YYYY-MM-DD').toDate(), availabilityOptions)
    if (currentIsValid) return

    const first = firstAvailableDate(availabilityOptions)
    if (first) {
      formik.setFieldValue('reservationDate', moment(first).format('YYYY-MM-DD'))
    } else if (current) {
      formik.setFieldValue('reservationDate', '')
    }
  }, [maxValidFrom, minValidTo, operationHours])

  if (services.length === 0) return null

  const themeColor = resolveThemeColor(project?.themeColor)
  const buttonColor = resolveThemeColor(project?.buttonColor)
  const fieldClass =
    'mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-sm font-medium text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-300/80'
  const labelClass = 'block text-sm font-medium text-gray-700'
  const sectionIntroClass = 'text-xs font-semibold uppercase tracking-wider text-gray-500'
  const errorTextClass = 'mt-1.5 text-sm text-red-600'
  const sectionCardClass = 'rounded-2xl border border-gray-200/70 bg-white p-5 shadow-sm shadow-gray-900/5 sm:p-6'

  const selectedDateLabel = formik.values.reservationDate
    ? moment(formik.values.reservationDate, 'YYYY-MM-DD')
        .locale(i18n.language?.startsWith('id') ? 'id' : 'en')
        .format('dddd, DD MMMM YYYY')
    : ''
  const selectedTimeLabel =
    availableTimes.find((slot) => slot.value === formik.values.reservationTime)?.time || ''
  const locationLabel =
    formik.values.isExternal === 'true'
      ? project?.externalName || t('externalFallback')
      : formik.values.isExternal === 'false'
        ? project?.offsiteName || t('offsiteFallback')
        : '-'
  const remarkLabel = project?.remarkLabel || t('remarkFallback')
  const requiredTitle = t('required')

  return (
    <>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={closeConfirmationModal}
        onConfirm={() => {
          setIsConfirmationModalOpen(false)
          bookService(formik.values)
        }}
        title={t('confirmationTitle')}
        message={t('duplicateBookingMessage')}
      />
      <div
        id="booking-form"
        className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white text-left text-gray-900 shadow-sm shadow-gray-900/5"
      >
        <header
          className="border-b border-gray-100 px-5 py-4 sm:px-8 sm:py-5"
          style={{
            background: `linear-gradient(135deg, ${themeColor}08 0%, transparent 55%), linear-gradient(to bottom, #fafafa, #ffffff)`,
          }}
        >
          <Stepper step={step} themeColor={themeColor} steps={STEPS} />
        </header>

        <div className="px-5 py-5 sm:px-8 sm:py-6" ref={stepContentRef}>
          <FormikProvider value={formik}>
            <form
              className="flex flex-col gap-5 max-sm:pb-24"
              action="#!"
              onSubmit={(e) => {
                e.preventDefault()
                handlePrimary()
                return false
              }}
            >
              {step === STEP_SERVICES && (
                <>
                <section className={sectionCardClass} id="form-services">
                  <p className={sectionIntroClass}>{t('servicesAndPackages')} <RequiredMark title={requiredTitle} /></p>
                  <button
                    type="button"
                    className={`${fieldClass} mt-3 flex items-center justify-between text-left font-semibold text-gray-800 ring-1 ring-inset ring-gray-200/80 hover:bg-gray-50`}
                    aria-haspopup="dialog"
                    aria-expanded={isServicePickerOpen}
                    onClick={() => {
                      setServiceSearchQuery('')
                      setServiceCategoryTab(recentServices.length > 0 ? 'recent' : 'all')
                      setIsServicePickerOpen(true)
                    }}
                  >
                    <span>{t('addOrChangeServices')}</span>
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('open')}</span>
                  </button>
                  <ServicePickerModal
                    isOpen={isServicePickerOpen}
                    onClose={closeServicePicker}
                    themeColor={themeColor}
                    buttonColor={buttonColor}
                    fieldClass={fieldClass}
                    selectedServices={selectedServices}
                    filteredServices={filteredServices}
                    serviceCategoryTabs={serviceCategoryTabs}
                    serviceCategoryTab={serviceCategoryTab}
                    onCategoryTabChange={setServiceCategoryTab}
                    serviceSearchQuery={serviceSearchQuery}
                    onSearchQueryChange={setServiceSearchQuery}
                    onToggleService={toggleServiceInPicker}
                  />
                  {selectedServices.length > 0 ? (
                    <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-gray-50/90 p-3">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        {t('selectedServices', { count: selectedServices.length })}
                      </p>
                      <ul className="flex flex-col gap-2">
                        {selectedServices.map((service) => {
                          const subscriptionExpiryDate =
                            service.isSubscription && service.subscriptionDuration && formik.values.reservationDate
                              ? moment(formik.values.reservationDate)
                                  .add(service.subscriptionDuration, 'days')
                                  .startOf('day')
                              : null
                          return (
                            <li
                              key={service.serviceId}
                              className="flex min-w-0 items-center justify-between gap-3 rounded-md border border-gray-100 bg-white py-2 pl-3 pr-2 shadow-sm"
                              style={{ borderLeftWidth: 3, borderLeftColor: themeColor }}
                            >
                              <div className="min-w-0 flex-1">
                                <span className="block text-sm font-medium leading-snug text-gray-900">
                                  {service.title}
                                </span>
                                {subscriptionExpiryDate ? (
                                  <span className="mt-0.5 block text-[11px] font-medium text-gray-500">
                                    {t('subscriptionExpiresOn')}{' '}
                                    <span className="font-semibold text-gray-700">
                                      {subscriptionExpiryDate.format('DD MMM YYYY')}
                                    </span>
                                  </span>
                                ) : null}
                              </div>
                              <button
                                type="button"
                                className="inline-flex shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
                                aria-label={t('removeService', { title: service.title })}
                                onClick={() =>
                                  setSelectedServices(selectedServices.filter((s) => s.serviceId !== service.serviceId))
                                }
                              >
                                <X className="h-4 w-4" strokeWidth={2} />
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-gray-500">{t('noServicesSelected')}</p>
                  )}
                  {formik.touched.serviceIds && formik.errors.serviceIds ? (
                    <p className={errorTextClass}>{String(formik.errors.serviceIds)}</p>
                  ) : null}
                </section>

                {!allowExternalBooking && (
                  <div
                    className="rounded-lg border border-amber-200/80 bg-amber-50/90 px-4 py-3 text-sm text-amber-900"
                    role="status"
                  >
                    {t('externalNotAllowed')}
                  </div>
                )}

                {project?.allowExternalBooking && project?.allowOffsiteBooking && (
                  <section className={sectionCardClass}>
                    <p className={sectionIntroClass}>
                      {t('fields.location')} <RequiredMark title={requiredTitle} />
                    </p>
                    <div className="mt-4 flex flex-wrap gap-3">
                      {([
                        { value: 'false', label: project?.offsiteName || t('offsiteFallback'), disabled: false },
                        { value: 'true', label: project?.externalName || t('externalFallback'), disabled: !allowExternalBooking },
                      ] as const).map((option) => {
                        const isActive = formik.values.isExternal === option.value
                        return (
                          <button
                            key={option.value}
                            type="button"
                            aria-pressed={isActive}
                            disabled={option.disabled}
                            onClick={() => {
                              formik.setFieldValue('isExternal', option.value)
                              formik.setFieldTouched('isExternal', true, false)
                            }}
                            className={`rounded-xl border px-5 py-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                              isActive ? '' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                            style={
                              isActive
                                ? { ...getReadablePrimaryButtonStyle(themeColor), borderColor: themeColor }
                                : undefined
                            }
                          >
                            {option.label}
                          </button>
                        )
                      })}
                    </div>
                    {formik.touched.isExternal && formik.errors.isExternal ? (
                      <p className={errorTextClass}>{String(formik.errors.isExternal)}</p>
                    ) : null}
                  </section>
                )}

                {project?.allowExternalBooking && formik.values.isExternal === 'true' && (
                  <section className={sectionCardClass}>
                    <label className={labelClass} htmlFor="booking-attended-address">
                      {t('fields.serviceAddress')} <RequiredMark title={requiredTitle} />
                    </label>
                    <textarea
                      id="booking-attended-address"
                      name="attendedAddress"
                      rows={3}
                      value={formik.values.attendedAddress}
                      className={`${fieldClass} min-h-[5.5rem] resize-y`}
                      onChange={onHandleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.attendedAddress && formik.errors.attendedAddress ? (
                      <p className={errorTextClass}>{String(formik.errors.attendedAddress)}</p>
                    ) : null}
                  </section>
                )}
                </>
              )}

              {step === STEP_SCHEDULE && (
                <>
                  <section className={sectionCardClass} id="form-datetime">
                    <p className={sectionIntroClass}>{t('fields.serviceDate')} <RequiredMark title={requiredTitle} /></p>
                    <div className="mt-4">
                      <DatePickerField
                        value={formik.values.reservationDate}
                        onSelect={(date) =>
                          formik.setFieldValue('reservationDate', moment(date).format('YYYY-MM-DD'))
                        }
                        maxValidFrom={maxValidFrom}
                        minValidTo={minValidTo}
                        operationHours={operationHours}
                        themeColor={themeColor}
                      />
                    </div>
                    {selectedDateLabel ? (
                      <div
                        className="mt-4 rounded-xl border px-4 py-3 text-sm"
                        style={{ borderColor: `${themeColor}33`, backgroundColor: `${themeColor}0d` }}
                      >
                        <span className="text-gray-600">{t('selectedDate')}</span>
                        <p className="mt-0.5 font-semibold text-gray-900">{selectedDateLabel}</p>
                      </div>
                    ) : null}
                    {formik.touched.reservationDate && formik.errors.reservationDate ? (
                      <p className={errorTextClass}>{String(formik.errors.reservationDate)}</p>
                    ) : null}
                  </section>

                  <section className={sectionCardClass}>
                    <p className={sectionIntroClass}>{t('fields.serviceTime')}</p>
                    {availableTimes.length > 0 ? (
                      <div className="mt-4 grid grid-cols-3 gap-2.5 sm:grid-cols-4">
                        {availableTimes.map((time) => {
                          const isActive = formik.values.reservationTime === time.value
                          return (
                            <button
                              key={time.value}
                              type="button"
                              name="reservationTime"
                              aria-pressed={isActive}
                              className={`rounded-xl border px-2 py-3 text-sm font-semibold transition-colors ${
                                isActive ? '' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                              style={
                                isActive
                                  ? { ...getReadablePrimaryButtonStyle(themeColor), borderColor: themeColor }
                                  : undefined
                              }
                              onClick={() => formik.setFieldValue('reservationTime', time.value)}
                            >
                              {time.time}
                            </button>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="mt-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
                        {t('noTimeSlots')}
                      </p>
                    )}
                    {formik.touched.reservationTime && formik.errors.reservationTime ? (
                      <p className={errorTextClass}>{String(formik.errors.reservationTime)}</p>
                    ) : null}
                  </section>
                </>
              )}

              {step === STEP_EXTRAS && (
                <>
                  {customFormFields.length > 0 ? (
                    <section className={sectionCardClass}>
                      <p className={sectionIntroClass}>{t('additionalInformation')}</p>
                      <div className="mt-4 space-y-6">
                        {customFormFields.map((form, index) => (
                          <CustomForm key={index} form={form} formik={formik} themeColor={themeColor} />
                        ))}
                      </div>
                    </section>
                  ) : null}

                  <section className={sectionCardClass}>
                    <label className={labelClass} htmlFor="booking-remark">
                      {remarkLabel} <span className="font-normal text-gray-500">{t('optional')}</span>
                    </label>
                    <textarea
                      id="booking-remark"
                      name="remark"
                      rows={3}
                      value={formik.values.remark}
                      className={`${fieldClass} min-h-[5.5rem] resize-y`}
                      onChange={onHandleChange}
                    />
                  </section>
                </>
              )}

              {step === STEP_DETAILS && (
                <section className={sectionCardClass}>
                  <p className={sectionIntroClass}>{t('steps.details')}</p>

                  <div className="mt-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/60 p-4">
                    <p className="text-sm font-medium text-gray-700">{t('qr.title')}</p>
                    <p className="mt-1 text-xs text-gray-500">{t('qr.hint')}</p>
                    <input
                      ref={qrInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleQrFileChange}
                    />
                    <button
                      type="button"
                      className="mt-3 inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-60"
                      disabled={qrStatus.state === 'loading'}
                      onClick={() => qrInputRef.current?.click()}
                    >
                      {qrStatus.state === 'loading'
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <QrCode className="h-4 w-4" />}
                      {t('qr.upload')}
                    </button>
                    {qrStatus.message ? (
                      <p className={qrStatus.state === 'error' ? errorTextClass : 'mt-1.5 text-sm text-green-600'}>
                        {qrStatus.message}
                      </p>
                    ) : null}
                  </div>

                  <div className="mt-4 space-y-4">
                    <div>
                      <label className={labelClass} htmlFor="booking-customer-name">
                        {t('fields.fullName')} <RequiredMark title={requiredTitle} />
                      </label>
                      <input
                        id="booking-customer-name"
                        type="text"
                        name="customerName"
                        autoComplete="name"
                        value={formik.values.customerName}
                        className={fieldClass}
                        onChange={onHandleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.customerName && formik.errors.customerName ? (
                        <p className={errorTextClass}>{String(formik.errors.customerName)}</p>
                      ) : null}
                    </div>
                    <div>
                      <span className={labelClass}>{t('fields.phoneNumber')} <RequiredMark title={requiredTitle} /></span>
                      <div className="mt-1.5 flex gap-2">
                        <select
                          className={`${fieldClass} !w-20 shrink-0 !px-2`}
                          name="customerPhonePrefix"
                          value={formik.values.customerPhonePrefix}
                          onChange={onHandleChange}
                        >
                          <option value="+62">+62</option>
                        </select>
                        <input
                          type="tel"
                          name="customerPhoneNo"
                          inputMode="tel"
                          autoComplete="tel-national"
                          value={formik.values.customerPhoneNo}
                          className={`${fieldClass} min-w-0 flex-1`}
                          onChange={onHandleChange}
                          onBlur={formik.handleBlur}
                        />
                      </div>
                      {formik.touched.customerPhoneNo && formik.errors.customerPhoneNo ? (
                        <p className={errorTextClass}>{String(formik.errors.customerPhoneNo)}</p>
                      ) : null}
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="booking-customer-email">
                        {t('fields.email')} <span className="font-normal text-gray-500">{t('optional')}</span>
                      </label>
                      <input
                        id="booking-customer-email"
                        type="email"
                        name="customerEmail"
                        autoComplete="email"
                        value={formik.values.customerEmail}
                        className={fieldClass}
                        onChange={onHandleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.customerEmail && formik.errors.customerEmail ? (
                        <p className={errorTextClass}>{String(formik.errors.customerEmail)}</p>
                      ) : null}
                    </div>
                  </div>
                </section>
              )}

              {step === STEP_REVIEW && (
                <section className={sectionCardClass}>
                  <p className={sectionIntroClass}>{t('reviewYourBooking')}</p>
                  <dl className="mt-4 space-y-4 text-sm">
                    <ReviewRow label={t('fields.services')}>
                      <ul className="space-y-1">
                        {selectedServices.map((service) => (
                          <li key={service.serviceId} className="font-medium text-gray-900">
                            {service.title}
                          </li>
                        ))}
                      </ul>
                    </ReviewRow>
                    {project?.allowExternalBooking && project?.allowOffsiteBooking ? (
                      <ReviewRow label={t('fields.location')}>{locationLabel}</ReviewRow>
                    ) : null}
                    <ReviewRow label={t('fields.date')}>{selectedDateLabel || '-'}</ReviewRow>
                    <ReviewRow label={t('fields.time')}>{selectedTimeLabel || '-'}</ReviewRow>
                    <ReviewRow label={t('fields.name')}>{formik.values.customerName || '-'}</ReviewRow>
                    <ReviewRow label={t('fields.phone')}>
                      {formik.values.customerPhoneNo
                        ? `${formik.values.customerPhonePrefix} ${formik.values.customerPhoneNo}`
                        : '-'}
                    </ReviewRow>
                    {formik.values.customerEmail ? (
                      <ReviewRow label={t('fields.email')}>{formik.values.customerEmail}</ReviewRow>
                    ) : null}
                    {formik.values.isExternal === 'true' && formik.values.attendedAddress ? (
                      <ReviewRow label={t('fields.address')}>{formik.values.attendedAddress}</ReviewRow>
                    ) : null}
                    {customFormFields.map((form) => {
                      const value = (formik.values.customForm as Record<string, unknown>)?.[form.key]
                      if (!value) return null
                      return (
                        <ReviewRow key={form.key} label={form.label}>
                          {String(value)}
                        </ReviewRow>
                      )
                    })}
                    {formik.values.remark ? (
                      <ReviewRow label={remarkLabel}>{formik.values.remark}</ReviewRow>
                    ) : null}
                  </dl>
                </section>
              )}

              {stepSummary.length > 0 ? (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                >
                  <p className="font-semibold">
                    {stepSummary.length === 1
                      ? t('pleaseCompleteOne')
                      : t('pleaseCompleteMany', { count: stepSummary.length })}
                  </p>
                  <ul className="mt-1.5 list-disc space-y-0.5 pl-5">
                    {stepSummary.map((item) => (
                      <li key={item.name}>{item.label}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div
                className="z-20 flex items-center justify-between gap-3 border-t border-gray-100 bg-white/95 px-5 py-4 backdrop-blur max-sm:fixed max-sm:inset-x-0 sm:sticky sm:-mx-8 sm:px-8"
                style={{
                  bottom: keyboardInset,
                  paddingBottom: keyboardInset > 0 ? '1rem' : 'calc(env(safe-area-inset-bottom) + 1rem)',
                }}
              >
                {step > STEP_DETAILS ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                    onClick={() => goToStep(step - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden />
                    <span>{t('back')}</span>
                  </button>
                ) : (
                  <span />
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold shadow-md transition hover:brightness-105 active:brightness-95 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:brightness-100 sm:flex-none sm:px-8"
                  style={getReadablePrimaryButtonStyle(buttonColor)}
                >
                  {step < STEP_REVIEW ? (
                    <>
                      <span>{t('continue')}</span>
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    </>
                  ) : isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      <span>{t('booking')}</span>
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" aria-hidden />
                      <span>{t('bookNow')}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </FormikProvider>
        </div>
      </div>
    </>
  )
}

function Stepper({
  step,
  themeColor,
  steps,
}: {
  step: number
  themeColor: string
  steps: { id: string; label: string }[]
}) {
  const total = steps.length
  const currentLabel = steps[step]?.label ?? ''
  const progress = Math.min(((step + 1) / total) * 100, 100)
  const accent = getReadablePrimaryButtonStyle(themeColor).backgroundColor
  return (
    <div className="mt-4">
      <span className="block text-sm font-semibold text-gray-900">{currentLabel}</span>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full transition-all duration-300 ease-out"
          style={{ width: `${progress}%`, backgroundColor: accent }}
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={step + 1}
        />
      </div>
    </div>
  )
}

function ReviewRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0 sm:flex-row sm:gap-4">
      <dt className="w-32 shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="min-w-0 flex-1 text-gray-900 [overflow-wrap:anywhere]">{children}</dd>
    </div>
  )
}

export default BookingForm
